import { useMemo, useState } from 'react';
import { Button, Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import type { DirTaskProcessCategoryType } from '@econtractor/types';
import { useTheme } from 'context/ThemeContext';
import { useSettings } from 'context/SettingsContext';
import { selectStylesDark, selectStylesLigth } from 'utils/helpers/structs/selectStyles';
import SolarIcon from 'components/icons/SolarIcon';
import EToolTip from 'components/others/EToolTip';
import type { ProcessCategoryOption } from './types';

interface ProcessCategoryFilterProps {
    filterCategoryId: number | null;
    setFilterCategoryId: (id: number | null) => void;
}

export const ProcessCategoryFilterSelect = ({
    filterCategoryId,
    setFilterCategoryId,
}: ProcessCategoryFilterProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { darkTheme } = useTheme();
    const { localSettings } = useSettings();
    const selectTheme = darkTheme ? selectStylesDark : selectStylesLigth;

    const categoryOptions = useMemo<ProcessCategoryOption[]>(
        () =>
            ((localSettings?.DIRS?.TaskProcessCategory || []) as DirTaskProcessCategoryType[])
                .filter((item) => !item.hidden || item.ID === filterCategoryId)
                .map((item) => ({ value: item.ID, label: item.categoryName })),
        [localSettings?.DIRS, filterCategoryId],
    );

    return (
        <>
            <label className="form-label text-muted fs-12 mb-1">{t('filterCategory')}</label>
            <Select<ProcessCategoryOption, false>
                isClearable
                options={categoryOptions}
                value={categoryOptions.find((opt) => opt.value === filterCategoryId) || null}
                onChange={(opt) => setFilterCategoryId(opt?.value ? Number(opt.value) : null)}
                placeholder={t('filterCategory')}
                className="w-100"
                styles={{
                    ...selectTheme,
                    menuPortal: (base) => ({ ...base, zIndex: 2100 }),
                }}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                menuPosition="fixed"
            />
        </>
    );
};

const ProcessCategoryFilter = ({ filterCategoryId, setFilterCategoryId }: ProcessCategoryFilterProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { darkTheme } = useTheme();
    const { localSettings } = useSettings();
    const selectTheme = darkTheme ? selectStylesDark : selectStylesLigth;
    const [show, setShow] = useState(false);
    const [draftCategoryId, setDraftCategoryId] = useState<number | null>(filterCategoryId);
    const filterActive = Boolean(filterCategoryId);

    const categoryOptions = useMemo<ProcessCategoryOption[]>(
        () =>
            ((localSettings?.DIRS?.TaskProcessCategory || []) as DirTaskProcessCategoryType[])
                .filter((item) => !item.hidden || item.ID === draftCategoryId)
                .map((item) => ({ value: item.ID, label: item.categoryName })),
        [localSettings?.DIRS, draftCategoryId],
    );

    return (
        <>
            <EToolTip title={t('toolFilter')}>
                <span className="d-inline-block">
                    <Button
                        className="fs-14 px-2"
                        size="sm"
                        variant={filterActive ? 'danger' : 'primary'}
                        onClick={(e) => {
                            e.currentTarget.blur();
                            setDraftCategoryId(filterCategoryId);
                            setShow(true);
                        }}
                    >
                        <SolarIcon name="funnel" className="fs-14 p-0" />
                    </Button>
                </span>
            </EToolTip>

            <Offcanvas show={show} onHide={() => setShow(false)} placement="end">
                <Offcanvas.Header closeButton className="border-bottom">
                    <Offcanvas.Title>{t('toolFilter')}</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                    <div className="off-filter-form d-flex flex-column h-100">
                        <div className="off-filter-content px-3">
                            <label className="form-label text-muted fs-12 mb-1">{t('filterCategory')}</label>
                            <Select<ProcessCategoryOption, false>
                                isClearable
                                options={categoryOptions}
                                value={categoryOptions.find((opt) => opt.value === draftCategoryId) || null}
                                onChange={(opt) => setDraftCategoryId(opt?.value ? Number(opt.value) : null)}
                                placeholder={t('filterCategory')}
                                className="w-100"
                                styles={{
                                    ...selectTheme,
                                    menuPortal: (base) => ({ ...base, zIndex: 2100 }),
                                }}
                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                menuPosition="fixed"
                            />
                        </div>
                        <div className="p-3 offcanvas-footer off-filter-footer">
                            <div className="d-flex justify-content-end gap-2">
                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        setFilterCategoryId(null);
                                        setShow(false);
                                    }}
                                >
                                    {t('common:buttons.reset')}
                                </Button>
                                <Button
                                    variant="info"
                                    className="min100px"
                                    onClick={() => {
                                        setFilterCategoryId(draftCategoryId);
                                        setShow(false);
                                    }}
                                >
                                    {t('common:buttons.apply')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
};

export default ProcessCategoryFilter;
